/**
 * ReportLanguageSelect - discoverable report/message-language control surfaced
 * in the upload flow (pirx-j05p, feedback #4b). The report language was only
 * reachable via Settings, which users found hard to discover; this places the
 * existing control at the point of use.
 *
 * It binds to the SAME preference as Settings (localStorage cache + best-effort
 * backend sync), so the two surfaces stay consistent. It adds no new language
 * capability.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateUserPreferences } from "@/services/api-client";
import {
  type AppLanguage,
  getStoredReportLanguage,
  setReportLanguage as persistReportLanguage,
} from "@/utils/language-preference";

interface ReportLanguageSelectProps {
  disabled?: boolean;
}

export function ReportLanguageSelect({
  disabled = false,
}: ReportLanguageSelectProps) {
  const { t } = useTranslation(["upload", "common"]);
  const [language, setLanguage] = useState<AppLanguage>(getStoredReportLanguage);

  const languageLabels: Record<AppLanguage, string> = {
    pl: t("common:language.names.pl"),
    en: t("common:language.names.en"),
  };

  const handleChange = (value: string) => {
    const next: AppLanguage = value === "en" ? "en" : "pl";
    setLanguage(next);
    persistReportLanguage(next);
    // Best-effort durable sync so the choice follows the user across browsers;
    // localStorage already holds it for the current device (and is what the
    // upload submit reads). Mirrors Settings' report-language handler.
    void updateUserPreferences({ report_language: next }).catch(() => {});
  };

  return (
    <div className="mt-6" data-test="upload-report-language">
      <label
        htmlFor="upload-report-language"
        className="block text-sm font-medium text-[var(--foreground)]"
      >
        {t("upload:reportLanguage.label")}
      </label>
      <p className="mt-1 text-xs text-[var(--muted-foreground)]">
        {t("upload:reportLanguage.hint")}
      </p>
      <Select
        items={languageLabels}
        value={language}
        disabled={disabled}
        onValueChange={(value) => handleChange(String(value))}
      >
        <SelectTrigger
          id="upload-report-language"
          data-test="upload-report-language-select"
          className="mt-2 sm:w-auto"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="pl">{languageLabels.pl}</SelectItem>
          <SelectItem value="en">{languageLabels.en}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
